import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { TechBackground } from "../components/TechBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;
const POLL_INTERVAL_MS = 2000;

const cardAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const logDevError = (...args) => {
  if (process.env.NODE_ENV !== "production") console.error(...args);
};

function usePaymentPolling(sessionId) {
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(res.data.payment_status)) { setState("error"); return; }
      } catch (e) { logDevError("Payment status poll failed", e); }
      attempts += 1;
      if (attempts >= MAX_POLLS) { setState("pending"); return; }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return state;
}

const STATUS_VIEWS = {
  checking: {
    Icon: Loader2, iconClass: "text-cyan animate-spin mx-auto",
    title: "CONFIRMING YOUR MEMBERSHIP",
    text: "Securing your access level. This only takes a moment…",
    home: false,
  },
  paid: {
    Icon: CheckCircle2, iconClass: "text-emerald mx-auto",
    title: "WELCOME TO THE NETWORK",
    text: "Your membership is active. Your store credit and member benefits are now unlocked.",
    home: true,
  },
  pending: {
    Icon: Loader2, iconClass: "text-cyan mx-auto",
    title: "PAYMENT PROCESSING",
    text: "Your payment is still processing. You'll receive confirmation shortly.",
    home: true,
  },
  error: {
    Icon: XCircle, iconClass: "text-white/50 mx-auto",
    title: "SOMETHING WENT WRONG",
    text: "We couldn't confirm this session. Please try again.",
    home: true,
  },
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const state = usePaymentPolling(params.get("session_id"));
  const view = STATUS_VIEWS[state];

  return (
    <Shell testid="payment-success">
      <view.Icon size={54} className={view.iconClass} />
      <H>{view.title}</H>
      <P>{view.text}</P>
      {view.home && <Home />}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel">
      <XCircle size={54} className="text-white/50 mx-auto" />
      <H>CHECKOUT CANCELLED</H>
      <P>No charge was made. Your access level is still waiting whenever you're ready.</P>
      <Home />
    </Shell>
  );
}

const Shell = ({ children, testid }) => (
  <div className="relative min-h-[100svh] flex items-center justify-center px-6 grain" data-testid={testid}>
    <TechBackground variant="hero" />
    <motion.div {...cardAnimation} className="relative z-10 glass max-w-lg w-full p-10 text-center">
      {children}
    </motion.div>
  </div>
);
const H = ({ children }) => <h1 className="font-display text-2xl sm:text-3xl font-700 tracking-tight text-white mt-6">{children}</h1>;
const P = ({ children }) => <p className="mt-4 text-dim text-sm leading-relaxed">{children}</p>;
const Home = () => (
  <Link to="/" className="cta-outline inline-flex mt-8 px-8 py-3 text-sm" data-testid="back-home-btn">
    RETURN TO MEMBERSHIP
  </Link>
);
